import DateFnsUtils from "@date-io/date-fns"
import { DEFAULT_LOCALE, LOCALES } from "app/language/model"
import { languageSVC } from "app/language/service"
import { getLocale, getRegionalizedLocale } from "app/language/tools/common"
import { getStore } from "app/store/store"
import {
  add,
  differenceInDays,
  eachDayOfInterval,
  format,
  formatDistanceToNow as formatDistanceToNowFns,
  fromUnixTime,
  isAfter,
  isBefore,
  isPast,
  parseISO,
  sub
} from "date-fns"
import ar from "date-fns/locale/ar"
import en from "date-fns/locale/en-US"
import es from "date-fns/locale/es"
import fr from "date-fns/locale/fr"
import {
  DATE_FNS_DATETIME_FORMAT_BY_LOCALE,
  DATE_FNS_DATETIME_LONG_FORMAT_BY_LOCALE,
  DATE_FNS_DATE_FORMAT_BY_LOCALE,
  DATE_FNS_DATE_LONG_FORMAT_BY_LOCALE,
  DATE_FNS_LOCALE_BY_LOCALE,
  IDateFormatParams,
  IDateLike,
  IDateUnit,
  IS_12_HOURS_TIME_BY_LOCALE,
  IUtilsByLocale
} from "../model"

class FrLocalizedUtils extends DateFnsUtils {
  public getDatePickerHeaderText(date: Date) {
    return format(date, "d MMM yyyy", { locale: this.locale })
  }
}

class EsLocalizedUtils extends DateFnsUtils {
  public getDatePickerHeaderText(date: Date) {
    return format(date, "d MMM yyyy", { locale: this.locale })
  }
}

class ArLocalizedUtils extends DateFnsUtils {
  public getDatePickerHeaderText(date: Date) {
    return format(date, "d MMM yyyy", { locale: this.locale })
  }
}

const UTILS_BY_LOCALE: IUtilsByLocale = {
  en: DateFnsUtils,
  fr: FrLocalizedUtils,
  es: EsLocalizedUtils,
  ar: ArLocalizedUtils
}

export function getDateFnsUtils(): DateFnsUtils {
  return UTILS_BY_LOCALE[languageSVC.getLocale()]
}

export function getDateFnsAdapter(): string | object | undefined {
  const locale = getLocale()
  switch (locale) {
    case LOCALES.EN: {
      return en
    }
    case LOCALES.FR: {
      return fr
    }
    case LOCALES.ES: {
      return es
    }
    case LOCALES.AR: {
      return ar
    }
    default: {
      throw Error(`DateFns locale "${locale}" not supported yet`)
    }
  }
}

export function getDateFnsDateFormat(params?: IDateFormatParams): string {
  let formatMap = DATE_FNS_DATE_FORMAT_BY_LOCALE
  if (params) {
    const { displayTime, displayLong } = params
    if (displayTime) {
      formatMap = displayLong ? DATE_FNS_DATETIME_LONG_FORMAT_BY_LOCALE : DATE_FNS_DATETIME_FORMAT_BY_LOCALE
    } else if (displayLong) {
      formatMap = DATE_FNS_DATE_LONG_FORMAT_BY_LOCALE
    }
  }
  const locale = getStore().getState().language.locale
  const dateFnsDateFormat = formatMap[locale]
  if (dateFnsDateFormat) {
    return dateFnsDateFormat
  }
  console.warn(`getDateFnsDateFormat(): unknown locale "${locale}"`)
  return formatMap[DEFAULT_LOCALE]
}

export function is12HoursTimeFormat(): boolean {
  const locale = getStore().getState().language.locale
  const is12Hours = IS_12_HOURS_TIME_BY_LOCALE[locale]
  if (is12Hours !== undefined) {
    if (!is12Hours && getRegionalizedLocale() === "fr-CA") {
      return true
    }
    return is12Hours
  }
  console.warn(`is12HoursTimeFormat(): unknown locale "${locale}"`)
  return IS_12_HOURS_TIME_BY_LOCALE[DEFAULT_LOCALE]
}

export function getRepositoryDate(date: string | null): Date | null {
  if (!date) {
    return null
  }
  return getDateFromISOString(date)
}

export function getDateFromISOString(isoDate: string): Date {
  if ((isoDate as any) instanceof Date) {
    return isoDate as any
  }
  if (typeof isoDate !== "string" || !isoDate) {
    throw Error("You must provide a non empty string (ISO) to get the date")
  }
  return parseISO(isoDate)
}

export function formatDate(date: number | Date | string, params?: IDateFormatParams): string {
  if (!date) {
    return ""
  }
  if (typeof date === "string") {
    if (!date) {
      return ""
    }
    date = getDateFromISOString(date)
  }
  if (typeof date !== "number" && !(date instanceof Date)) {
    return ""
  }
  const result = format(date, getDateFnsDateFormat(params), {
    locale: getDateFnsLocale()
  })
  return params && typeof params.prefix === "string" && params.prefix !== "" ? `${params.prefix}${result}` : result
}

export function formatDistanceToNow(date: Date | number | string, params?: IDateFormatParams): string {
  if (typeof date === "string") {
    if (!date) {
      return ""
    }
    date = getDateFromISOString(date)
  }
  if (typeof date !== "number" && !(date instanceof Date)) {
    return ""
  }
  let result: string
  result = formatDistanceToNowFns(date, {
    locale: getDateFnsLocale(),
    addSuffix: true
  })
  if (params && params.prefix) {
    result = `${params.prefix}${result}`
  } else if (result && result.length > 2) {
    result = result[0].toUpperCase() + result.slice(1)
  }
  return result
}

export function getDateFnsLocale(): any {
  const locale = getStore().getState().language.locale
  const dateFnsLocale = DATE_FNS_LOCALE_BY_LOCALE[locale]
  if (dateFnsLocale) {
    return dateFnsLocale
  }
  console.warn(`getDateFnsLocale(): unknown locale "${locale}"`)
  return DATE_FNS_LOCALE_BY_LOCALE[DEFAULT_LOCALE]
}

export function isDateBefore(date1: IDateLike, date2: IDateLike, checkTime?: boolean): boolean {
  date1 = checkTime ? getDate(date1) : getDateWithResetTime(getDate(date1))
  date2 = checkTime ? getDate(date2) : getDateWithResetTime(getDate(date2))
  return isBefore(date1, date2)
}

export function isDateAfter(date1: IDateLike, date2: IDateLike, checkTime?: boolean): boolean {
  date1 = checkTime ? getDate(date1) : getDateWithResetTime(getDate(date1))
  date2 = checkTime ? getDate(date2) : getDateWithResetTime(getDate(date2))
  return isAfter(date1, date2)
}

export function dateAdd(date: IDateLike, amount: number, timeUnit: IDateUnit): Date {
  return add(getDate(date), getDuration(amount, timeUnit))
}

export function dateSubstract(date: IDateLike, amount: number, timeUnit: IDateUnit): Date {
  return sub(getDate(date), getDuration(amount, timeUnit))
}

export function getDate(date: IDateLike): Date {
  if (typeof date === "string" && date !== "") {
    return getDateFromISOString(date)
  }
  if (typeof date === "number") {
    return fromUnixTime(date / 1000)
  }
  if (date instanceof Date) {
    return new Date(date)
  }
  throw Error(`Cannot convert date "${date}"`)
}

export function areSameDate(d1: Date | undefined | null, d2: Date | undefined | null): boolean {
  if (!d1 && !d2) {
    return true
  }
  if (!d1 || !d2) {
    return false
  }
  return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate()
}

export function getDifferenceInMilliSeconds(startDate: Date, endDate: Date): number {
  if (!startDate || !endDate || endDate < startDate) {
    return 0
  }
  return endDate.getTime() - startDate.getTime()
}

export function getDifferenceInSeconds(startDate: Date, endDate: Date): number {
  return getDifferenceInMilliSeconds(endDate, startDate) / 1000
}

export function getDifferenceInHours(startDate: Date, endDate: Date): number {
  return getDifferenceInSeconds(endDate, startDate) / 3600
}

export function getDifferenceInDay(startDate: Date, endDate: Date): number {
  return getDifferenceInHours(endDate, startDate) / 24
}

export function getDateWithMinTime<T extends Date | null>(date: T): T {
  if (!date) {
    return date
  }
  return getDateWithResetTime(date) as T
}

export function getDateWithMaxTime<T extends Date | null>(date: T): T {
  if (!date) {
    return date
  }
  return getDateWithResetTime(date, "23:59:59") as T
}

/**
 * Returns days of week included by the period
 * 0 => Sunday, ..., 1 => Monday, ...etc, 6 => Saturday
 **/
export function getAllWeekDaysOfPeriod(start: Date, end: Date): number[] {
  const dayCount = getDifferenceInDay(start, end)
  if (dayCount > 7) {
    return [0, 1, 2, 3, 4, 5, 6]
  }
  return eachDayOfInterval({ start, end })
    .map(d => d.getDay())
    .sort()
}

export function isDateInThePast(date: Date): boolean {
  return isPast(date)
}

export function getIntervalDurationInDays(start: Date, end: Date): number {
  return differenceInDays(start, end)
}

export const isValidDate = (date: any): boolean => {
  if (Object.prototype.toString.call(date) === "[object Date]") {
    return !isNaN(date.getTime())
  }
  return false
}

const getDateWithResetTime = (date: Date, time?: string): Date =>
  new Date(date.toString().replace(/[0-9][0-9]:[0-9][0-9]:[0-9][0-9]/, time || "00:00:00"))

function getDuration(amount: number, timeUnit: IDateUnit): Duration {
  const duration: Duration = {}
  switch (timeUnit) {
    case "seconds": {
      duration.seconds = amount
      break
    }
    case "minutes": {
      duration.minutes = amount
      break
    }
    case "hours": {
      duration.hours = amount
      break
    }
    case "days": {
      duration.days = amount
      break
    }
    case "weeks": {
      duration.weeks = amount
      break
    }
    case "months": {
      duration.months = amount
      break
    }
    case "years": {
      duration.years = amount
      break
    }
    default: {
      throw Error(`Time unit ${timeUnit} not found. Choose in: seconds, minutes, hours, days, weeks, months, years`)
    }
  }
  return duration
}
